import { v4 as uuidv4 } from "uuid";
import { create } from "zustand";
import { Plan, PlanDetail, PlanInfo } from "../../../types/plan";
import config from "../../../utils/config";
import {
  callChatApiV4,
  ChatMessageV4,
  ChatSuccessResponseV4,
  MatchItemV4,
} from "../types/aiChatClientV4";
import { highlightSearchResults } from "../utils/aiChatUtils";
import { formatSpaceInfo } from "../utils/formatV4Results";

// V4 endpoint is provided explicitly
const CHAT_API_URL =
  config.aiChatV4Endpoint || `http://localhost:8000/process-query-v3`;

export interface SpacesName {
  name: string;
  dbas: string[];
}

export interface AIChatV4 {
  messages: ChatMessageV4[];
  historyMessagesToSend: ChatMessageV4[];
  isLoading: boolean;
  isSearching: boolean;
  error: string | null;
  currentSearchResult: ChatSuccessResponseV4 | null;
  currentHighlight: string | null;
  clearCurrentHighlight: () => void;
  plansInfo: PlanInfo | null;
  distanceData: any | null;

  // Actions
  sendMessage: (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode?: boolean,
    selectedSpaces?: string[]
  ) => Promise<void>;

  clearError: () => void;
  internalCADViewerAICallback: (chatMessage: ChatMessageV4) => void;
  setPlansInfoAndDistanceData: (plansInfo: any, distanceData: any) => void;
  clearHistoryMessagesToSend: () => void;
}

export const useAIChatStoreV4 = create<AIChatV4>((set, get) => ({
  messages: [],
  historyMessagesToSend: [],
  isLoading: false,
  isSearching: false,
  error: null,
  currentSearchResult: null,
  currentHighlight: null,
  plansInfo: null,
  distanceData: null,

  setPlansInfoAndDistanceData: (plansInfo, distanceData) => {
    set({ plansInfo, distanceData });
  },

  clearError: () => {
    set({ error: null, currentSearchResult: null });
  },

  internalCADViewerAICallback: (chatMessage: ChatMessageV4) => {
    const { currentHighlight } = get();

    if (!chatMessage.result) return;

    if (currentHighlight !== chatMessage.uuid) {
      // Result is already in the expected format: { targetPlanInfo?, searchResults }
      highlightSearchResults(chatMessage.result as any);
      set({ currentHighlight: chatMessage.uuid ?? null });
    } else {
      // Clear existing highlights
      highlightSearchResults({ targetPlanInfo: undefined, searchResults: [] });
      set({ currentHighlight: null });
    }
  },

  sendMessage: async (
    prompt: string,
    spacesNames: SpacesName[],
    selectedPlan?: Plan,
    selectedPlanDetail?: PlanDetail,
    useGlobalMode: boolean = false,
    selectedSpaces?: string[]
  ) => {
    if (!prompt.trim()) return;

    const { historyMessagesToSend } = get();

    const metadata = {
      searchMode: useGlobalMode ? ("global" as const) : ("current" as const),
      selectedSpaces: selectedSpaces || [],
      planInfo: !useGlobalMode && selectedPlan && selectedPlanDetail ? {
        propertyName: selectedPlan.propertyName,
        planName: selectedPlanDetail.planName,
      } : undefined,
      timestamp: new Date().toISOString(),
    };

    const newUserMessage: ChatMessageV4 = {
      uuid: uuidv4(),
      role: "user",
      content: prompt,
      metadata,
    };

    // Keep current messages state to pass as history
    const historyToSend = historyMessagesToSend;

    // Optimistically update UI with user message
    set((state) => ({
      messages: [...state.messages, newUserMessage],
      historyMessagesToSend: [...state.historyMessagesToSend, newUserMessage],
      isLoading: true,
      error: null,
      currentSearchResult: null,
    }));

    try {
      console.log(
        "Calling API V4 at:",
        CHAT_API_URL,
        "with prompt:",
        prompt,
        "and history:",
        historyToSend.map((msg) => ({
          role: msg.role,
          content: msg.content,
        }))
      );

      const requestUuid = uuidv4();
      const result: ChatSuccessResponseV4 = await callChatApiV4(
        CHAT_API_URL,
        prompt,
        "hybrid",
        100                       // build into config later
      );

      console.log("API V4 Response:", result);

      // Handle the search results response
      if (result && result.success) {
        // Convert V4 response to the format expected by the existing display component
        const matches = [
          ...(result.response?.exact_matches || []),
          ...(result.response?.partial_matches || []),
        ];

        // Function to extract information from query interpretation item
        const getQueryInterpretationInfo = (items: MatchItemV4[]) => {
          if (items.length === 0) return undefined;

          const firstItem = items[0];
          const formattedInfo = formatSpaceInfo(firstItem);

          return {
            spaceNumber: firstItem.space_number,
            spaceHeader: firstItem.tenant_name,
            summary: {
              gla: `${formattedInfo.area}`,
            },
            detailTabs: [],
          };
        };

        // Function to convert match items
        const convertMatches = (matches: MatchItemV4[]) => {
          return matches.map((m) => {
            const formattedInfo = formatSpaceInfo(m);
            return {
              spaceID: formattedInfo.spaceID,
              area: formattedInfo.area,
              tenant: formattedInfo.tenant,
              distance: formattedInfo.distance || 0,
              totalRentPSF: formattedInfo.totalRentPSF,
              leaseEndDate: formattedInfo.leaseEndDate,
              totalRent: formattedInfo.totalRent,
            };
          });
        };

        const convertedResult = {
          targetPlanInfo: getQueryInterpretationInfo(
            result.response?.query_interpretation || []
          ),
          searchResults: convertMatches(matches),
        };

        console.log({ convertedResult });

        // Success response - create message with search results
        const assistantMessage: ChatMessageV4 = {
          uuid: requestUuid,
          role: "assistant",
          content:
            result.response.message ??
            `Found ${matches.length} spaces matching your criteria`,
          rawContent: JSON.stringify(result, null, 2),
          result: convertedResult as any,
        };

        set((state) => ({
          messages: [...state.messages, assistantMessage],
          isLoading: false,
          isSearching: false,
          currentSearchResult: result,
          searchResults: [],
        }));

        // Apply highlighting
        setTimeout(() => {
          get().internalCADViewerAICallback(assistantMessage);
        }, 200);

        console.log(
          "Search results added to message:",
          assistantMessage.result
        );
      } else {
        // Error response or no success
        const errorMessage = "Search could not be processed";

        const errorAssistantMessage: ChatMessageV4 = {
          uuid: requestUuid,
          role: "assistant",
          content: errorMessage,
          rawContent: JSON.stringify(result, null, 2),
        };

        set((state) => ({
          messages: [...state.messages, errorAssistantMessage],
          isLoading: false,
          isSearching: false,
        }));
      }
    } catch (err) {
      console.error("Chat API V4 Error in store:", err);
      const errorMessage =
        err instanceof Error ? err.message : "An unknown error occurred.";

      // Create an error message to display in chat
      const errorAssistantMessage: ChatMessageV4 = {
        uuid: uuidv4(),
        role: "assistant",
        content: `Sorry, I encountered an error: ${errorMessage}`,
      };

      set((state) => ({
        messages: [...state.messages, errorAssistantMessage],
        isLoading: false,
        isSearching: false,
        error: errorMessage,
      }));
    }
  },

  clearHistoryMessagesToSend: () => {
    set({ historyMessagesToSend: [], messages: [] });
  },

  clearCurrentHighlight: () => {
    set({ currentHighlight: null });
  },
}));
